/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 */
import React from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { spacing } from '../../shared/theme/spacing'
import { typography } from '../../shared/theme/typography'
import { ticketColors } from '../../shared/theme/ticketColors';
import { hasCustomerCreditLimit } from '../application/customerCreditLimit';

type Props = {
  balance: number;
  creditLimit: number;
  formatMoney: (n: number) => string;
};

function gaugeColor(pct: number): string {
  if (pct >= 1) return ticketColors.bad;
  if (pct >= 0.8) return ticketColors.gold;
  if (pct >= 0.6) return ticketColors.ink2;
  return ticketColors.ok;
}

function gaugeLabel(pct: number): string {
  if (pct >= 1) return 'Sobre el límite';
  if (pct >= 0.8) return 'Cerca del límite';
  if (pct >= 0.6) return 'Uso moderado';
  return 'Uso bajo';
}

export function CustomerCreditGauge({ balance, creditLimit, formatMoney }: Props) {
  if (!hasCustomerCreditLimit(creditLimit)) {
    return (
      <View style={styles.container}>
        <View style={styles.headerRow}>
          <Text style={styles.title}>Crédito usado</Text>
          <Text style={styles.noLimit}>Sin límite</Text>
        </View>
        <View style={styles.balanceRow}>
          <Text style={styles.balanceLabel}>Saldo actual</Text>
          <Text style={[styles.balanceAmount, balance > 0 && styles.balanceDebt]}>
            {formatMoney(balance)}
          </Text>
        </View>
      </View>
    );
  }

  const pct = Math.max(0, balance / creditLimit);
  const clampedPct = Math.min(pct, 1);
  const color = gaugeColor(pct);
  const label = gaugeLabel(pct);
  const available = Math.max(creditLimit - balance, 0);

  return (
    <View style={styles.container}>
      <View style={styles.headerRow}>
        <Text style={styles.title}>Crédito usado</Text>
        <Text style={[styles.pctBadge, { color }]}>{Math.round(pct * 100)}%</Text>
      </View>

      <View style={styles.gaugeTrack}>
        <View
          style={[
            styles.gaugeFill,
            {
              width: `${Math.round(clampedPct * 100)}%` as any,
              backgroundColor: color,
            },
          ]}
        />
        {pct > 1 ? (
          <View style={styles.overLimitIndicator} />
        ) : null}
      </View>

      <Text style={[styles.gaugeStatusLabel, { color }]}>{label}</Text>

      <View style={styles.metricsRow}>
        <View style={styles.metricCell}>
          <Text style={styles.metricLabel}>Saldo</Text>
          <Text style={[styles.metricValue, balance > 0 && { color: ticketColors.bad }]}>
            {formatMoney(balance)}
          </Text>
        </View>
        <View style={[styles.metricCell, styles.metricCellCenter]}>
          <Text style={styles.metricLabel}>Disponible</Text>
          <Text style={[styles.metricValue, available === 0 && { color: ticketColors.ink3 }]}>
            {formatMoney(available)}
          </Text>
        </View>
        <View style={[styles.metricCell, styles.metricCellRight]}>
          <Text style={styles.metricLabel}>Límite</Text>
          <Text style={styles.metricValue}>{formatMoney(creditLimit)}</Text>
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    gap: spacing.sm,
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: {
    ...typography.caption,
    color: ticketColors.ink2,
    fontWeight: '900',
    textTransform: 'uppercase',
    letterSpacing: 0.35,
  },
  noLimit: {
    ...typography.caption,
    color: ticketColors.ink3,
    fontStyle: 'italic',
  },
  pctBadge: {
    ...typography.body,
    fontWeight: '900',
    fontSize: 15,
  },
  gaugeTrack: {
    height: 10,
    borderRadius: 5,
    backgroundColor: ticketColors.rule,
    overflow: 'hidden',
    position: 'relative',
  },
  gaugeFill: {
    height: 10,
    borderRadius: 5,
  },
  overLimitIndicator: {
    position: 'absolute',
    right: 0,
    top: 0,
    bottom: 0,
    width: 4,
    backgroundColor: ticketColors.bad,
    borderRadius: 2,
  },
  gaugeStatusLabel: {
    ...typography.caption,
    fontWeight: '700',
    marginTop: -spacing.xs,
  },
  balanceRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  balanceLabel: {
    ...typography.body,
    color: ticketColors.ink2,
  },
  balanceAmount: {
    ...typography.body,
    color: ticketColors.ink,
    fontWeight: '700',
  },
  balanceDebt: {
    color: ticketColors.bad,
  },
  metricsRow: {
    flexDirection: 'row',
    paddingTop: spacing.xs,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: ticketColors.rule,
  },
  metricCell: {
    flex: 1,
    gap: 2,
  },
  metricCellCenter: {
    alignItems: 'center',
  },
  metricCellRight: {
    alignItems: 'flex-end',
  },
  metricLabel: {
    ...typography.caption,
    color: ticketColors.ink3,
    fontWeight: '600',
  },
  metricValue: {
    ...typography.body,
    color: ticketColors.ink,
    fontWeight: '700',
    fontSize: 13,
  },
});
